import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Util } from 'src/app/util';
import { Format, hiddenFormats, Record, streamingFormats } from 'src/app/models/record';
import { ButtonsConfiguration, InfoButtonConfiguration } from 'src/app/models/buttons-config';
import { HistoryItem } from 'src/app/models/history-user';
import { Params, UrlTree } from '@angular/router';
import { Action, ActionType } from 'src/app/models/action';
import { TypeAction } from 'src/app/models/record-external';
import { BookClubCarousel } from 'src/app/models/bookclubcarousel';
import { CheckoutDownload } from 'src/app/models/checkout-download';
import { ExperienceLink } from 'src/app/models/experience-link';
import { ExperienceResult } from 'src/app/models/experience-result';
import { ExperienceType } from 'src/app/models/experiences';
import { OleConfiguration } from 'src/app/models/ole-configuration';
import { BaseDropdownOptionsButtonComponent } from '../../base-dropdown-options-button/base-dropdown-options-button.component';
import { RecaptchaDialogComponent } from '../../../_shared/components/dialogs/recaptcha-dialog/recaptcha-dialog.component';
import { Subscription } from 'rxjs';
import { ReportDialogComponent } from 'src/app/_shared/components/dialogs/report-dialog/report-dialog.component';
import { ExternalVideoModel } from 'src/app/_core/state/dialogs/external-video/model/external-video-data.model';
import { LoginDialogComponent } from '../../../login/components/login-dialog/login-dialog.component';
import { HistoryFacade } from '../../../state/user/history/history.facade';
import { createWindowDeferred } from '../../../_core/utils/create-window-deferred';
import { AnalyticsService } from '../../../_core/analytics/analytics.service';

@Component({
  selector: 'app-more-option-button',
  templateUrl: './more-option-button.component.html',
  styleUrls: [
    './more-option-button.component.scss',
    '../base-button.scss',
    '../download-button/download-button.component.scss',
    '../certificate-button/certificate-button.component.scss',
    '../add-to-list-button/add-to-list-button.component.scss',
  ],
})
export class MoreOptionButtonComponent extends BaseDropdownOptionsButtonComponent implements OnChanges, OnDestroy {
  private readonly historyFacade = inject(HistoryFacade);
  private readonly amplitudeService = inject(AnalyticsService);

  @Input()
  recordItem: Record;

  @Input()
  historyItem: HistoryItem;

  @Input()
  isCheckoutItem = false;

  @Input()
  isHistoryItem = false;

  @Input()
  isHoldItem = false;

  @Input()
  isCareerPlan = false;

  @Input()
  certificateUrl: string;

  @Input()
  pageName: string;

  @Input()
  extras: Params;

  @Input()
  certificateSection: boolean;

  @Input() showCiteButton: boolean;
  @Input() showReportButton: boolean;
  @Input() showFavoriteButton: boolean = true;

  @ViewChild(RecaptchaDialogComponent) recaptchaDialog!: RecaptchaDialogComponent;

  @Output()
  removed = new EventEmitter<HistoryItem>();

  buttonsConfig: ButtonsConfiguration;
  showShareCertificateButton = false;

  private downloadFormat: string;

  private favoriteAddedSubscription: Subscription;
  private favoriteRemovedSubscription: Subscription;

  readonly isPhysicalItem = signal<boolean>(false);
  readonly showDeleteHistoryItemDialog = signal<boolean>(false);
  readonly historyItemToDelete = signal<HistoryItem>(null);

  ngOnInit() {
    super.record = this.recordItem;
    this.textDirection = this.localeService.getTextDirection();
    this.buttonsConfig = new InfoButtonConfiguration(this.configService.get().showReturnLoanButton);
    this.shareText = this.localeService.getLocalizedShareText(this.recordItem, this.configService.get().title);
    this.showShareButtons = this.configService.get().showSocialIcons;
    this.socialButtons = this.configService.shareIcons;
    this.checkout.set(this.checkoutsFacade.getCheckoutByRecordId(this.recordItem.id, this.issueDate, this.rhId));

    if (this.certificateSection && this.historyItem && this.historyItem.urlCertificate) {
      let defaultFormat: Format;
      const formats = this.recordItem.formats.filter((format) => {
        if (!defaultFormat && !hiddenFormats.includes(format)) {
          defaultFormat = format;
        }

        if (format === 'ACSM') {
          if (!this.configService.get().showAcsmButton) {
            return false;
          }
        }

        return !hiddenFormats.includes(format) && format !== defaultFormat;
      });

      this.showShareCertificateButton = formats.length > 0;
    }

    this.favoriteFacade.favoriteAdded$.subscribe((favoriteAdded) => {
      if (favoriteAdded?.recordId === this.record.id) {
        this.alreadyInFavorites = true;
      }
    });

    this.favoriteFacade.favoriteRemoved$.subscribe((favoriteRemoved) => {
      if (favoriteRemoved?.recordId === this.record.id) {
        this.alreadyInFavorites = false;
      }
    });

    this.retrieveFormat();

    this.alreadyInFavorites = this.favoriteFacade.getFavoriteByRecordId(this.record.id) !== undefined;

    if (this.record.resourceTypes.includes('TIPO_FISICO') && this.record.resourceTypes.length === 1) {
      this.isPhysicalItem.set(true);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.recordItem === undefined) {
      this.recordItem = { id: this.recordItem?.id };
      this.alreadyInFavorites = this.favoriteFacade.getFavoriteByRecordId(this.recordItem?.id) !== undefined;
    } else {
      const record = changes?.recordItem;
      this.alreadyInFavorites = this.favoriteFacade.getFavoriteByRecordId(record.currentValue?.id) !== undefined;
      this.retrieveFormat();
    }
  }

  ngOnDestroy(): void {
    this.favoriteAddedSubscription?.unsubscribe();
    this.favoriteRemovedSubscription?.unsubscribe();
  }

  doBlur() {
    // By default mat menu auto focus some menu items then need to blur the focus
    setTimeout(() => {
      (document.activeElement as HTMLElement).blur();
    }, 1);
  }

  copyToClipboard() {
    Util.copyToClipboard(Util.getOrigin() + '/info/' + this.recordItem.id);
    this.toastService.showToast('messages.copied', 'info');
  }

  alreadyCheckedOut(): boolean {
    if (!this.recordItem) {
      return false;
    }

    if (this.recordItem.containerData != null && this.isParentContainer()) {
      return false;
    }

    return this.checkoutsFacade.getCheckoutByRecordId(this.recordItem.id, this.issueDate, this.rhId) != null;
  }

  handleHistoryItemToDelete(historyItem: HistoryItem) {
    this.showDeleteHistoryItemDialog.set(true);
    this.historyItemToDelete.set(historyItem);
  }

  deleteHistoryItem() {
    this.showDeleteHistoryItemDialog.set(false);
    this.apiService.hideHistoryItem(this.historyItemToDelete().recordId, this.historyItem.type).subscribe({
      next: () => {
        this.removed.emit(this.historyItem);
        this.toastService.showToast('messages.visualizationHistory.remove');
        this.amplitudeService.sendUserActionEvent('item_removed_from_history', true);
      },
      error: (error) => {
        this.waiting = false;
        this.amplitudeService.sendUserActionEvent('item_removed_from_history', false);
        return console.error(error);
      },
    });
  }

  public shareCertificateOnLinkedin() {
    let url;
    const linkedinEndpoint = 'https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME';
    const andParam = '&';
    const paramNameKey = 'name=';
    const paramOrganizationIdKey = 'organizationId=';
    const paramIssueMonthKey = 'issueMonth=';
    const paramIssueYearKey = 'issueYear=';
    const paramCertUrlKey = 'certUrl=';
    const paramOrganizationIdValue = this.configService.getCertificationEntityId();

    const match = this.historyItem.urlCertificate.match(/_(\d+)\.pdf/);
    const date = match[1];
    const paramIssueYearValue = date.slice(-4);
    const paramIssueMonthValue = date.slice(-6, -4);

    const paramCertUrlValue =
      this.apiService.API_URL + '/records/download-certificate' + '?path=' + this.historyItem.urlCertificate;

    url =
      linkedinEndpoint +
      andParam +
      paramNameKey +
      this.recordItem.title +
      andParam +
      paramOrganizationIdKey +
      paramOrganizationIdValue +
      andParam +
      paramIssueMonthKey +
      paramIssueMonthValue +
      andParam +
      paramIssueYearKey +
      paramIssueYearValue +
      andParam +
      paramCertUrlKey +
      encodeURIComponent(paramCertUrlValue);

    window.open(url);
  }

  /*
   * Checkout section
   */
  @Output()
  disablerecordItem: EventEmitter<boolean> = new EventEmitter<boolean>();
  doCheckout() {
    const action: Action = {
      type: ActionType.CHECKOUT,
      recordId: this.recordItem.id,
    };

    this.doAction(
      () => {
        this.analyticsService.sendCheckoutStartEvent();
        this.checkoutsFacade
          .postCheckout(this.recordItem.id, this.getCurrentScreen(), this.issueDate, this.rhId)
          .subscribe(
            () => {
              this.waiting = false;
              this.toastService.showToast('messages.checkouts.new');
              this.analyticsService.sendCheckoutEndEvent();

              const hold = this.holdsFacade.getHoldByRecordId(this.recordItem.id, this.issueDate, this.rhId);

              if (hold) {
                hold.status = 'downloaded';
                this.holdsFacade.setHold(hold);
              }

              if (this.configService.get().openResourceOnBorrow) {
                this.download();
              }
            },
            (_err) => {
              this.waiting = false;
              if (_err.error.ERROR === 'ERROR_OD_FINDAWAY_LICENSE_EXPIRED') {
                this.disablerecordItem.emit(false);
              }
              throw _err;
            }
          );
      },
      action,
      true
    );
  }

  /*
   * Hold section
   */
  hold() {
    const action: Action = {
      type: ActionType.HOLD,
      recordId: this.recordItem.id,
    };

    this.doAction(
      () => {
        this.analyticsService.sendHoldStartEvent();
        this.holdsFacade
          .postHold(this.recordItem.id, this.getCurrentScreen(), this.issueDate, this.rhId)
          .subscribe(() => {
            this.toastService.showToast('messages.holds.new');
            this.analyticsService.sendHoldEndEvent();
            this.waiting = false;
          });
      },
      action,
      true
    );
  }

  /*
   * External link section
   */
  openExternalLink() {
    const isIframe = this.recordItem.externalLink.includes('<iframe');
    const isLearningExperience =
      !this.configService.shouldHideBookClub() &&
      this.recordItem.externalLink.includes(this.configService.get().bookClubUrl);

    if (isLearningExperience) {
      return this.doLearningExperienceAction();
    }

    const action: Action = {
      type: ActionType.OPEN_EXTERNAL,
      recordId: this.recordItem.id,
    };

    if (this.record.typeAction === TypeAction.OPEN_URL) {
      const [openWindow] = createWindowDeferred();
      openWindow(this.record.externalLink);
      return;
    }

    if (this.recordItem.typeAction === TypeAction.AWS_COURSES) {
      this.apiService
        .getExternalLink(this.recordItem.id)
        .subscribe((value) => (this.recordItem.externalLink = value.externalLink));
    }

    this.doAction(() => {
      this.apiService.remoteView(this.recordItem.id, this.from);
      if (isIframe) {
        return this.openDialogExternalLink();
      }

      location.href = this.recordItem.externalLink;
    }, action);
  }

  private openDialogExternalLink() {
    const externalVideoModel: ExternalVideoModel = {
      title: this.recordItem.title,
      link: this.recordItem.externalLink,
      format: this.recordItem.formats,
      userId: this.userService.getUserId(),
      resourceId: this.recordItem.id,
      isFromExternalDb: false,
    };

    this.externalVideoDialogFacade.openDialog(externalVideoModel);
  }

  private doLearningExperienceAction() {
    if (this.authService.isAuthenticated()) {
      this.getTokenAndRedirectToLearningExperience();
    } else {
      const customLoginUrl = this.configService.get().customLoginUrl;
      if (customLoginUrl) {
        this.authService.getExternalLoginUrl(customLoginUrl).subscribe((url) => {
          localStorage.setItem('returnFromLearningExperience', this.router.url);
          localStorage.setItem('redirectToLearningExperience', 'Y');
          localStorage.setItem('clubId', this.getClubId());
          window.location.href = url;
        });
      } else {
        this.openLoginDialog(false);
      }
    }
  }

  private openLoginDialog(showRegisterForm = false) {
    this.dialog
      .open(LoginDialogComponent, {
        data: {
          showRegisterForm: showRegisterForm,
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.getTokenAndRedirectToLearningExperience();
        } else {
          const action = Util.getAction();
          if (action) {
            Util.setAction({
              ...action,
              type: ActionType.NONE,
            });
          } else {
            Util.removeAction();
          }
        }
      });
  }

  private getTokenAndRedirectToLearningExperience() {
    const clubId = this.getClubId();

    const params = {
      patronId: this.userService.getUserId(),
      clubId: clubId,
    };

    this.apiService.getLinkToBookclub(params).subscribe((url) => {
      this.apiService.remoteView(this.recordItem.id, this.from);
      if (!url.includes('clubId')) {
        url = url + '&clubId=' + params['clubId'];
      }
      location.href = url;
    });
  }

  private getClubId() {
    return this.recordItem.learningExperienceId
      ? this.recordItem.learningExperienceId
      : this.recordItem.externalLink.slice(this.recordItem.externalLink.lastIndexOf('/') + 1);
  }

  /*
   * Download section
   */
  formats: Format[] = [];
  defaultFormat: Format;
  buttonLabel: string;

  readonly STREAMING_FORMATS: Format[] = streamingFormats;
  readonly HIDDEN_FORMATS: Format[] = hiddenFormats;
  private MAX_BOOK_CLUBS = 300;
  private ONLY_MODERATES = true;
  readonly ALLOWED_BOOKCLUBMODE_FORMATS: string[] = ['EPUB', 'PDF'];

  filterFormats() {
    this.formats = this.recordItem?.formats?.filter((format) => {
      if (format === 'ACSM') {
        if (!this.configService.get().showAcsmButton) {
          return false;
        }
      }
      return (
        !this.HIDDEN_FORMATS.includes(format) && format !== this.defaultFormat /*  ||
        (format === 'SCORM' && this.certificateItem) */
      );
    });
  }

  download(format?: string) {
    this.downloadFormat = format || this.defaultFormat;

    if (!this.recordItem.container) {
      this.rhId = null;
    }

    if (this.rssItem != null) {
      this.rhId = this.rssItem.rhId;
    }

    // Check recaptcha
    if (this.configService.get().enableDrmCaptcha && this.downloadFormat === 'ACSM') {
      this.recaptchaDialog.openDialog();

      return;
    }

    this.internalDownload();
  }

  private internalDownload(captchaToken?: string) {
    const checkout = this.checkoutsFacade.getCheckoutByRecordId(this.recordItem.id, this.issueDate, this.rhId);
    if (checkout) {
      const token = this.extractToken(checkout.downloadUrl);
      const userId = this.userService.getUserId();

      this.apiService.getLearningExperiencesAssociatedToRecordId(this.record.id, userId).subscribe((exps) => {
        const learningExperienceId = Util.extractLearningExperienceId(exps);
        this.apiService
          .downloadResource(
            checkout.id,
            userId,
            this.downloadFormat,
            token,
            this.record.id,
            learningExperienceId,
            captchaToken
          )
          .subscribe(
            (response) => {
              if (this.shouldShowTheModal(exps, this.downloadFormat)) {
                this.apiService.getOleConfiguration(userId).subscribe((oleConfig) => {
                  this.apiService.getBookClubs(userId, this.MAX_BOOK_CLUBS, this.ONLY_MODERATES).subscribe({
                    next: (bookClubsCollection) => {
                      this.handleLearningExperiences(exps, response, oleConfig, userId, bookClubsCollection);
                    },
                    error: () => {
                      this.handleLearningExperiences(exps, response, oleConfig, userId, []);
                    },
                  });
                });
              } else {
                if (this.downloadFormat.includes('ACSM')) {
                  this.checkoutsFacade.checkouts.find((_checkout) => _checkout.id === checkout.id).returnable = false;
                }

                if (this.downloadFormat.includes('HTML')) {
                  return this.openDialog(response.redirect);
                }

                if (typeof response.redirect === 'string') {
                  if (this.downloadFormat === 'SCORM') {
                    const backUrl = new URL(location.href);
                    backUrl.searchParams.set('showReturnButton', 'false');

                    response.redirect += `?backUrl=${encodeURIComponent(backUrl.toString())}`;
                  }

                  location.href = response.redirect;

                  if (this.downloadFormat.includes('MP3_LH')) {
                    this.toastService.showToast('messages.checkouts.download', 'info');
                  }
                }
              }
            },
            (err) => {
              throw err;
            }
          );
      });
    }
  }

  // Should show the modal if there are any experiences, and the bookclub mode is allowed
  // and the element is either and epub or a pdf. Don't show if it's ACSM.
  private shouldShowTheModal(exps: Record[], format: string) {
    if (format && format.toUpperCase() === 'ACSM') {
      return false;
    }
    return (
      exps &&
      this.configService.get().showBookclubMode &&
      this.recordItem.fileFormat &&
      this.ALLOWED_BOOKCLUBMODE_FORMATS.includes(this.recordItem.fileFormat.toUpperCase())
    );
  }

  private handleLearningExperiences(
    exps: Record[],
    response: CheckoutDownload,
    oleConfig: OleConfiguration,
    userId: string,
    bookClubCollections: BookClubCarousel[]
  ) {
    const miniExps: ExperienceResult[] = exps.map((exp) => ({
      learningExperienceId: exp.learningExperienceId,
      title: exp.title,
      externalUrl: new URL(exp.externalLink).origin,
    }));
    miniExps.sort((first, second) => first.title.localeCompare(second.title));
    const links: ExperienceLink[] = this.getExperienceLinks(response, miniExps, oleConfig, bookClubCollections);
    if (links.length === 1) {
      const exp = links[0];
      location.href = exp.url;
    } else {
      this.bookClubHistoryService
        .getBookClubHistoryByUserIdAndResourceId(userId, this.getRecordItemId())
        .subscribe((bookClubHistory) => {
          if (bookClubHistory.some((history) => history.dontShow === true) || response.dontShowModal) {
            let experienceId = bookClubHistory.length
              ? bookClubHistory[bookClubHistory.length - 1].learningExperienceId
              : '';
            if (experienceId === 'plain') {
              experienceId = '';
            }
            const link: ExperienceLink = links.find((element) => element.learningExperienceId === experienceId);
            location.href = link.url;
          } else {
            this.bookclubLinksDialogFacade.openDialog(this.recordItem, links, this.getRecordItemId());
          }
        });
    }
  }

  private getExperienceLinks(
    response: CheckoutDownload,
    experiencesArray: ExperienceResult[],
    oleConfig: OleConfiguration,
    bookClubCollections: BookClubCarousel[]
  ) {
    const moderateArray: BookClubCarousel[] = bookClubCollections.filter((bookClub) => bookClub.name === 'moderates');
    const links: ExperienceLink[] = [
      ...experiencesArray.map((exp) => ({
        name: exp.title,
        url: `${response.redirect}?bc=${exp.externalUrl}&c=${exp.learningExperienceId}`,
        learningExperienceId: exp.learningExperienceId,
        clicked: false,
        type: this.typeOfExperience(moderateArray, exp),
      })),
    ];
    if (this.isNormalUser(oleConfig, experiencesArray) || this.isCollaboratorUser(oleConfig, experiencesArray)) {
      return links;
    }
    if (experiencesArray.length > 1) {
      links.push({
        name: this.recordItem.title,
        url: response.redirect as string,
        learningExperienceId: '',
        clicked: false,
        type: ExperienceType.PLAIN,
      });
    }
    return links;
  }

  private getRecordItemId() {
    if (this.availability === undefined) {
      return this.recordItem.id;
    }
    return this.availability.recordId;
  }

  private typeOfExperience(
    moderateArray: BookClubCarousel[],
    exp: { externalUrl: string; learningExperienceId: string; title: string }
  ): ExperienceType {
    if (moderateArray.length === 0) {
      return ExperienceType.FOLLOWING;
    }
    const namesOfExperiences = moderateArray[0].bookClubs.map((experiences) => experiences.name);
    if (namesOfExperiences.some((name) => name === exp.title)) {
      return ExperienceType.MODERATES;
    }
    return ExperienceType.FOLLOWING;
  }

  private isNormalUser(
    oleConfig: OleConfiguration,
    experiencesArray: {
      externalUrl: string;
      learningExperienceId: string;
      title: string;
    }[]
  ) {
    return (
      oleConfig.canCreateExperiences === false &&
      oleConfig.canCreateUserGroups === false &&
      experiencesArray.length === 1
    );
  }

  private isCollaboratorUser(
    oleConfig: OleConfiguration,
    experiencesArray: {
      externalUrl: string;
      learningExperienceId: string;
      title: string;
    }[]
  ) {
    return (
      oleConfig.canCreateExperiences === false &&
      oleConfig.canCreateUserGroups === true &&
      experiencesArray.length === 1
    );
  }

  private openDialog(link) {
    if (!this.recordItem.container) {
      this.rhId = null;
    }

    if (this.rssItem != null) {
      this.rhId = this.rssItem.rhId;
    }

    const externalVideoModel: ExternalVideoModel = {
      title: this.recordItem.title,
      link: link,
      format: this.recordItem.formats,
      userId: this.userService.getUserId(),
      resourceId: this.recordItem.id,
      isFromExternalDb: false,
      checkoutId: this.checkoutsFacade.getCheckoutByRecordId(this.recordItem.id, this.issueDate, this.rhId).id,
    };

    this.externalVideoDialogFacade.openDialog(externalVideoModel);
  }

  private extractToken(url: string): string {
    const downloadUrl = url.replace(/^(https?:|)\/\//, '');
    const parsedUrl: UrlTree = this.router.parseUrl(downloadUrl);

    return parsedUrl.queryParams.token;
  }

  getActionLabel(format: Format): string {
    switch (format) {
      case 'EBOOK_STREAMING':
      case 'PDF':
      case 'EPUB':
        return 'button.actions.nubereader';
      case 'ACSM':
        return 'button.actions.acsm';
      case 'MP3':
      case 'MP4':
        return 'button.actions.nubeplayer';
      default:
        return 'common.open';
    }
  }

  private retrieveFormat() {
    this.defaultFormat = null;
    this.recordItem?.formats?.forEach((format) => {
      if (!this.defaultFormat && ![...this.HIDDEN_FORMATS].includes(format)) {
        this.defaultFormat = format;

        if (['MP3_LH', 'ACSM'].includes(format)) {
          this.buttonLabel = 'common.download';
        } else if (this.STREAMING_FORMATS.includes(format)) {
          this.buttonLabel = 'button.play';
        } /* else if (this.certificateItem) {
              this.buttonLabel = 'button.certificate';
            } */ else if (format === 'HTML') {
          this.buttonLabel = 'button.read';
        } else {
          this.buttonLabel = 'button.read';
        }
      }
    });

    this.filterFormats();
  }

  /*
   * History statistics section
   */
  openHistoryStatistics() {
    this.historyFacade.openStatsModal(this.historyItem, this.historyItem.type, this.historyItem.parentRecordId);
  }

  /*
   * Internal link section
   */
  navigatePage() {
    this.router.navigate([this.pageName], { queryParams: this.extras });
  }

  /*
   * Certificate section
   */
  openCertificate(url: string) {
    window.open(url, '_blank', 'noopener');
  }

  cancelHold() {
    this.doAction(
      () => {
        const hold = this.holdsFacade.getHoldByRecordId(this.recordItem.id, this.issueDate, this.rhId);
        this.holdsFacade.deleteHold(hold.id).subscribe(
          () => {
            this.toastService.showToast('messages.holds.cancel');
          },
          () => {},
          () => {
            this.waiting = false;
          }
        );
      },
      null,
      true
    );
  }

  citeRecord() {
    this.citationFacade.openCitationModal(this.record);
  }

  reportRecord() {
    this.dialog.open(ReportDialogComponent);
  }

  resolvedCaptcha(token: string) {
    if (token) {
      this.recaptchaDialog.closeDialog();
      this.internalDownload(token);
    }
  }
}
